import { useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import styled from 'styled-components';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import { IFaqsProps } from './IFAQS';
import Title from '../../../Title/Title';
import RichText from '../../../RichText/RichText';
import SchemaMarkup from './SchemaMarkup';

const Container = styled.div`
  & li,
  & ul,
  & ol,
  & p,
  & a,
  & span {
    @media (max-width: 767px) {
      font-size: 14px;
    }

    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
`;

function FaqItem({ title, content, index, activeIndex, setActiveIndex }) {
  const isActive = activeIndex === index;
  const router = useRouter();
  const currentPath = router.asPath;

  const toggleActive = () => {
    (window as any).dataLayer.push({
      event: 'faq_click',
      faq_page_url: currentPath,
      faq_action: isActive ? 'faq collapse' : 'faq expand',
      faq_label: title?.content,
    });
    setActiveIndex(isActive ? null : index);
  };

  return (
    <>
      <div
        onClick={toggleActive}
        className="tw-w-full tw-flex tw-flex-col tw-justify-between !tw-max-h-fit tw-font-[Avenir]"
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            toggleActive();
          }
        }}
      >
        <div className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none tw-font-[Avenir]">
          <Title
            component={title?.titleHTMLTag || 'h3'}
            size={title?.titleSize}
            weight={title?.titleWeight}
            alignment="left"
            className="tw-py-be-4"
            styles={{ margin: '0px' }}
          >
            {title?.content}
          </Title>

          <svg
            data-accordion-icon
            className={classNames('tw-w-[12px] tw-h-[6px] tw-shrink-0', {
              'tw-rotate-[-180deg]': !isActive,
            })}
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5 5 1 1 5" />
          </svg>
        </div>
      </div>
      <div
        className={classNames(
          'tw-border-0 tw-border-b-[1px] tw-border-solid tw-border-black tw-overflow-hidden tw-transition-[max-height] tw-duration-500',
          {
            'tw-max-h-[1px]': !isActive,
            'tw-max-h-[1500px]': isActive,
          }
        )}
      >
        <RichText content={content} className="tw-pb-be-4" />
      </div>
    </>
  );
}

function Faqs({ items, optionalLargeHeader, schema, anchorLinkID, margin = false }: IFaqsProps) {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={classNames(
        'tw-inline-block tw-max-w-[1172px] tw-w-[85%] tw-mx-auto tw-bg-white tw-text-be-dark-gray',
        getComponentMargin(margin)
      )}
    >
      <Container>
        {optionalLargeHeader?.content && (
          <Title
            component={optionalLargeHeader?.titleHTMLTag || 'h2'}
            size={optionalLargeHeader?.titleSize}
            weight={optionalLargeHeader?.titleWeight}
            alignment="center"
          >
            {optionalLargeHeader?.content}
          </Title>
        )}
        {schema && <SchemaMarkup items={items} />}
        {items?.map((item, index) => (
          <FaqItem
            key={index + 1}
            title={item.question}
            content={item.answer}
            index={index + 1}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        ))}
      </Container>
    </div>
  );
}

export default Faqs;
